// Chart styling shared by the on-screen charts and the off-screen copies the
// PDF export rasterises. Colours come from the site palette (--rs-* in
// Web.Site's site.css): every chart is a single series, so each one gets the
// brand blue, and the map is a one-hue blue ramp for magnitude.

export const palette = {
    blue: '#0c539a',
    navy: '#06192c',
    rampLow: '#dde8f4',
    ink: '#1b2733',
    muted: '#5b6876',
    grid: '#e3e8ee',
    baseline: '#c3cbd4',
    noData: '#eef1f4',
};

/** A row label is a string, or { v: code, f: name } for the map. */
export type ChartLabel = string | { v: string, f: string };
export type ChartRow = [ChartLabel, number];

export function labelOf(label: ChartLabel): string {
    return typeof label === 'object' && label !== null ? label.f : String(label);
}

/** The API sends Google Charts arrays: a header row, then [label, value] rows. */
export function rowsOf(data: unknown[] | undefined): ChartRow[] {
    return (data ?? []).slice(1) as ChartRow[];
}

/** Largest first; ties keep the API's order. */
export function sortedDescending(data: unknown[] | undefined): ChartRow[] {
    return rowsOf(data).slice().sort((a, b) => b[1] - a[1]);
}

/** Back into a Google Charts array, header first. */
export function toChartData(rows: ChartRow[]): unknown[] {
    return [['Category', 'Fatalities'], ...rows];
}

export function largest(data: unknown[] | undefined): ChartRow | null {
    const rows = sortedDescending(data);
    return rows.length > 0 && rows[0][1] > 0 ? rows[0] : null;
}

const textStyle = { color: palette.muted, fontName: 'Roboto', fontSize: 12 };

const axis = {
    textStyle,
    baselineColor: palette.baseline,
    gridlines: { color: palette.grid },
    minorGridlines: { count: 0 },
};

const common = {
    backgroundColor: 'transparent',
    fontName: 'Roboto',
    colors: [palette.blue],
    legend: { position: 'none' },
    tooltip: { textStyle: { color: palette.ink, fontName: 'Roboto', fontSize: 13 } },
};

export const geoOptions = {
    ...common,
    region: 'US',
    resolution: 'provinces',
    colorAxis: { colors: [palette.rampLow, palette.blue, palette.navy] },
    datalessRegionColor: palette.noData,
    defaultColor: palette.noData,
    legend: { textStyle },
    keepAspectRatio: true,
};

/** Labels every `labelEvery` years, so they fit on one line at card width. */
export function yearsOptions(rowCount: number, labelEvery = rowCount > 8 ? 2 : 1) {
    return {
        ...common,
        lineWidth: 2,
        pointSize: 8,
        chartArea: { left: 48, right: 16, top: 16, bottom: 40 },
        hAxis: { ...axis, gridlines: { color: 'transparent' }, showTextEvery: labelEvery, maxAlternation: 1 },
        vAxis: { ...axis, format: '0', viewWindow: { min: 0 } },
    };
}

/** Horizontal bars, room for long category names on the left. */
export function barOptions(rowCount: number) {
    return {
        ...common,
        bar: { groupWidth: rowCount > 6 ? '70%' : '55%' },
        chartArea: { left: '42%', right: 24, top: 8, bottom: 32 },
        hAxis: { ...axis, format: '0', viewWindow: { min: 0 } },
        vAxis: { ...axis, textStyle: { ...textStyle, color: palette.ink } },
    };
}

/** Bars need a fixed pitch to stay readable however many categories there are. */
export function barHeight(rowCount: number) {
    return Math.max(220, rowCount * 34 + 48);
}
